import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// User roles and their permissions
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  SALES: 'sales',
  WAREHOUSE: 'warehouse',
  CONSULTANT: 'consultant',
  HR: 'hr',
  CUSTOMER_SERVICE: 'customer_service',
  PRODUCTION: 'production',
  B2B_USER: 'b2b_user',
  B2C_USER: 'b2c_user'
};

// Module permissions for each role
export const ROLE_PERMISSIONS = {
  [USER_ROLES.ADMIN]: [
    'dashboard', 'users', 'user_roles', 'stock', 'stock_card', 'stock_movement',
    'warehouse', 'shelf', 'invoices', 'orders', 'work_orders', 'settings',
    'b2b', 'b2c', 'production', 'reports', 'consultant', 'warehouse_terminal',
    'customer_relations', 'hr'
  ],
  [USER_ROLES.MANAGER]: [
    'dashboard', 'stock', 'stock_card', 'stock_movement', 'warehouse', 'shelf',
    'invoices', 'orders', 'work_orders', 'b2b', 'b2c', 'production', 'reports'
  ],
  [USER_ROLES.SALES]: [
    'dashboard', 'stock', 'orders', 'invoices', 'b2b', 'b2c', 'reports'
  ],
  [USER_ROLES.WAREHOUSE]: [
    'dashboard', 'stock', 'stock_movement', 'warehouse', 'shelf', 'warehouse_terminal'
  ],
  [USER_ROLES.CONSULTANT]: [
    'dashboard', 'work_orders', 'consultant'
  ],
  [USER_ROLES.HR]: [
    'dashboard', 'hr'
  ],
  [USER_ROLES.CUSTOMER_SERVICE]: [
    'dashboard', 'customer_relations'
  ],
  [USER_ROLES.PRODUCTION]: [
    'dashboard', 'production', 'stock'
  ],
  [USER_ROLES.B2B_USER]: [
    'dashboard', 'b2b'
  ],
  [USER_ROLES.B2C_USER]: [
    'dashboard', 'b2c'
  ]
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('companyToken');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setIsAuthenticated(true);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
        logout();
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      // API login only - no test users fallback
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          username: username, 
          password: password
        }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('companyToken', data.token);
        localStorage.setItem('userData', JSON.stringify(data.user));
        setIsAuthenticated(true);
        setUser(data.user);
        return { success: true };
      } else {
        const errorData = await response.json();
        return { 
          success: false, 
          error: errorData.message || 'Giriş başarısız. Lütfen kullanıcı adı ve şifrenizi kontrol edin.' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('companyToken');
    localStorage.removeItem('userData');
    setIsAuthenticated(false);
    setUser(null);
  };

  const hasPermission = (permission) => {
    if (!user || !user.role) return false;
    const userPermissions = ROLE_PERMISSIONS[user.role] || [];
    return userPermissions.includes(permission);
  };

  const getTestUsers = () => {
    return [
      {
        name: 'Sistem Yöneticisi',
        username: 'admin@testfirma1.com',
        password: 'admin123',
        role: 'admin'
      },
      {
        name: 'Satış Müdürü',
        username: 'manager@testfirma1.com',
        password: 'manager123',
        role: 'manager'
      },
      {
        name: 'Satış Temsilcisi',
        username: 'sales@testfirma1.com',
        password: 'sales123',
        role: 'sales'
      },
      {
        name: 'Depo Sorumlusu',
        username: 'warehouse@testfirma1.com',
        password: 'warehouse123',
        role: 'warehouse'
      },
      {
        name: 'Danışman',
        username: 'consultant@testfirma1.com',
        password: 'consultant123',
        role: 'consultant'
      },
      {
        name: 'İK Uzmanı',
        username: 'hr@testfirma1.com',
        password: 'hr123',
        role: 'hr'
      },
      {
        name: 'Üretim Sorumlusu',
        username: 'production@testfirma1.com',
        password: 'production123',
        role: 'production'
      },
      {
        name: 'B2B Kullanıcısı',
        username: 'b2b@testfirma1.com',
        password: 'b2b123',
        role: 'b2b_user'
      },
      {
        name: 'B2C Kullanıcısı',
        username: 'b2c@testfirma1.com',
        password: 'b2c123',
        role: 'b2c_user'
      }
    ];
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    hasPermission,
    getTestUsers,
    USER_ROLES,
    ROLE_PERMISSIONS
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 